import { useEffect, useMemo, useState } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useAppDispatch, useAppSelector } from '../../../../../../redux/store';
import { updateSupplier } from '../../../../../../redux/services/suppliersService';
import { openDillToast } from '../../../../../../utils/helpers';
import { Supplier } from '@dill/dill-shared';
import { getUserDetails } from '../../../../../../redux/services/authService';
import { DillButton, DillToggle } from '../../../../../general/Dill';

interface DiscountSettings {
  isDiscountOnCardPayments: boolean;
  isDiscountOnBankPayments: boolean;
  useErpTermsDiscount: boolean;
}

interface DiscountSettingsSectionProps {
  supplier?: Supplier | null;
}

const getInitialSettings = (supplier?: Supplier | null): DiscountSettings => ({
  isDiscountOnCardPayments: supplier?.discountSettings?.isDiscountOnCardPayments ?? false,
  // isDiscountOnBankPayments: supplier?.discountSettings?.isDiscountOnBankPayments ?? true,
  isDiscountOnBankPayments: true,
  useErpTermsDiscount: supplier?.discountSettings?.useErpTermsDiscount ?? false,
});

const TOGGLE_ROWS: {
  key: keyof DiscountSettings;
  label: string;
  description: string;
  integrationType?: string;
}[] = [
  {
    key: 'isDiscountOnCardPayments',
    label: 'Discounts on Card Payments',
    description: 'Apply early payment discounts when customers pay with a credit or debit card.',
  },
  {
    key: 'isDiscountOnBankPayments',
    label: 'Discounts on Bank Payments',
    description: 'Apply early payment discounts when customers pay via bank transfer (ACH).',
  },
  {
    key: 'useErpTermsDiscount',
    label: 'Use Infor Terms Discounts',
    description:
      "Offer the early payment discount and discount date from each invoice's terms in Infor.",
    integrationType: 'INFOR_DIST_SX',
  },
];

export const DiscountSettingsSection = ({ supplier }: DiscountSettingsSectionProps) => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.auth.user);

  const canEdit = useMemo(() => {
    if (!currentUser?.email || !supplier) return false;
    const email = currentUser.email.toLowerCase();
    const supplierEmail = supplier.email?.toLowerCase() ?? '';
    const adminEmails = (supplier.adminSupplierEmails ?? []).map((e) => e.toLowerCase());
    return (
      email === supplierEmail ||
      adminEmails.includes(email) ||
      (currentUser.roles?.includes('DILL_ADMIN') ?? false)
    );
  }, [currentUser, supplier]);

  const [settings, setSettings] = useState<DiscountSettings>(() => getInitialSettings(supplier));
  const [initialSettings, setInitialSettings] = useState<DiscountSettings>(() =>
    getInitialSettings(supplier)
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const newSettings = getInitialSettings(supplier);
    setSettings(newSettings);
    setInitialSettings(newSettings);
  }, [supplier]);

  const settingsChanged = useMemo(
    () =>
      settings.isDiscountOnCardPayments !== initialSettings.isDiscountOnCardPayments ||
      settings.isDiscountOnBankPayments !== initialSettings.isDiscountOnBankPayments ||
      settings.useErpTermsDiscount !== initialSettings.useErpTermsDiscount,
    [settings, initialSettings]
  );

  const handleSave = async () => {
    setSaving(true);
    const result = await dispatch(
      updateSupplier({
        supplierId: supplier?.id || '',
        supplierDetails: {
          discountSettings: settings,
        },
      })
    );
    setSaving(false);

    if (result.type === 'supplier/update/fulfilled') {
      await dispatch(getUserDetails());
      openDillToast({ type: 'SUCCESS', message: 'Discount settings saved' });
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to save discount settings' });
    }
  };

  const toggleRows = TOGGLE_ROWS.filter(
    (row) => !row.integrationType || row.integrationType === supplier?.integrationType
  );

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-gray-900">Discount Settings</h4>
        <p className="text-xs text-gray-400 mt-0.5">
          Configure which payment methods are eligible for early payment discounts.
        </p>
      </div>

      <div className="flex flex-col gap-1 flex-1">
        {toggleRows.map((row, i) => (
          <label
            key={row.key}
            className={`flex items-center justify-between gap-4 py-3 cursor-pointer ${
              i < toggleRows.length - 1 ? 'border-b border-gray-100' : ''
            }`}>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-800">{row.label}</p>
              <p className="text-[11px] text-gray-400 mt-0.5">{row.description}</p>
            </div>
            <div data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
              <DillToggle
                value={!!settings[row.key]}
                onChange={() => setSettings((prev) => ({ ...prev, [row.key]: !prev[row.key] }))}
                isDisabled={row.key === 'isDiscountOnBankPayments' || !canEdit}
              />
            </div>
          </label>
        ))}
      </div>

      <div className="mt-4 flex justify-end">
        <div data-tooltip-id={!canEdit ? 'no-permission-tooltip' : undefined}>
          <DillButton
            onClick={handleSave}
            label="Save changes"
            isLoading={saving}
            isDisabled={!canEdit || !settingsChanged}
          />
        </div>
      </div>

      {!canEdit && (
        <ReactTooltip
          id="no-permission-tooltip"
          place="top"
          variant="dark"
          opacity="100%"
          style={{ borderRadius: '8px', fontSize: '12px', maxWidth: '260px' }}>
          You do not have permission to make changes
        </ReactTooltip>
      )}
    </div>
  );
};
